/**
 * Página de Política de Privacidade
 * 
 * DESCRIÇÃO:
 * Página que exibe a política de privacidade da aplicação.
 * Criada para atender ao requisito de aceite obrigatório
 * durante o processo de cadastro.
 * 
 * FUNCIONALIDADES:
 * - Exibir política de privacidade completa
 * - Design responsivo e acessível
 * - Link para voltar ao cadastro
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/signup" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao cadastro
          </Link>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              Política de Privacidade
            </h1>
          </div>
          <p className="text-gray-600">
            Última atualização: {new Date().toLocaleDateString('pt-BR')}
          </p>
        </div>

        {/* Conteúdo da Política */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl">
              Como protegemos e utilizamos suas informações
            </CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none space-y-6">
            
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                1. Informações que Coletamos
              </h3>
              <p className="text-gray-700">
                Coletamos as seguintes informações quando você se cadastra em nosso sistema:
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                <li>Nome completo (opcional)</li>
                <li>Nome da clínica</li>
                <li>Endereço de email</li>
                <li>Dados de uso da plataforma</li>
                <li>Informações de leads e pacientes inseridas por você</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                2. Como Utilizamos suas Informações
              </h3>
              <p className="text-gray-700">
                Utilizamos suas informações para:
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                <li>Fornecer acesso ao sistema CRM</li>
                <li>Gerenciar sua conta e preferências</li>
                <li>Comunicar atualizações importantes</li>
                <li>Melhorar nossos serviços</li>
                <li>Cumprir obrigações legais</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                3. Compartilhamento de Informações
              </h3>
              <p className="text-gray-700">
                Não compartilhamos suas informações pessoais com terceiros, exceto:
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                <li>Quando necessário para cumprir a lei</li>
                <li>Para proteger nossos direitos e segurança</li>
                <li>Com provedores de serviços essenciais (sob acordo de confidencialidade)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                4. Segurança dos Dados
              </h3>
              <p className="text-gray-700">
                Implementamos medidas de segurança técnicas e organizacionais para proteger 
                suas informações contra acesso não autorizado, alteração, divulgação ou destruição.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                5. Seus Direitos (LGPD)
              </h3>
              <p className="text-gray-700">
                De acordo com a Lei Geral de Proteção de Dados, você tem direito a:
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                <li>Acessar seus dados pessoais</li>
                <li>Corrigir dados incompletos ou incorretos</li>
                <li>Solicitar a exclusão de dados</li>
                <li>Revogar o consentimento</li>
                <li>Portabilidade dos dados</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                6. Retenção de Dados
              </h3>
              <p className="text-gray-700">
                Mantemos suas informações pelo tempo necessário para fornecer nossos serviços 
                ou conforme exigido por lei. Dados de leads e pacientes são mantidos enquanto 
                sua conta estiver ativa.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                7. Cookies e Tecnologias Similares
              </h3>
              <p className="text-gray-700">
                Utilizamos cookies e tecnologias similares para melhorar a experiência de uso, 
                manter sessões de login e analisar o desempenho da plataforma.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                8. Contato
              </h3>
              <p className="text-gray-700">
                Para questões sobre esta política de privacidade ou seus dados pessoais, 
                entre em contato conosco através do email:{' '}
                <a 
                  href="mailto:contato@genscrm.com.br" 
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  contato@genscrm.com.br
                </a>
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                9. Alterações nesta Política
              </h3>
              <p className="text-gray-700">
                Podemos atualizar esta política periodicamente. Notificaremos sobre mudanças 
                significativas através de email ou aviso em nosso sistema.
              </p>
            </section>

          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Button asChild className="bg-blue-600 hover:bg-blue-700">
            <Link to="/signup">
              Voltar ao Cadastro
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;